// The 3D view's move gizmo (docs/claude/handles.md, *The move gizmo*): whether it
// shows and on what, its arrows where the view draws them, the one under the
// pointer, the handle a press on one works and what its work is beyond that
// handle (`gizmoRule`: its figure, snap, typed angle, chip, prompt, what it
// rebuilds, its move session's step), and its paint. handleWork.ts runs that work
// as it runs every 3D handle's: the drag, a click to arm, a typed figure, Esc,
// one undo step.
import { type Vec2 } from '../../shared/types';
import { drawingDimStyle } from '../dimFormat';
import {
  drawGizmo,
  faceCatches,
  GIZMO_AXES,
  type GizmoArrow,
  gizmoArrowAt,
  type GizmoAxis,
  type GizmoCatch,
  gizmoLayout,
  gizmoReadout,
  heightCatches,
  onRing,
  ringLayout,
  snapGizmoDelta,
  snapTurn,
  turnBetween,
  turnReadout,
  vertexCatches,
} from '../gizmo';
import {
  gridStepMm,
  instanceToWorld,
} from '../geometry';
import {
  cagePointGizmo,
  type Handle,
  hostHeights,
  type NamedHeight,
  type Pt3,
  rayToAxis,
  rayToLevel,
} from '../handles';
import { tr } from '../i18n';
import { roomSnapFaces } from '../roomSnap';
import { selectedOf } from '../selection';
import { selectedSupportNow } from './benchSupportTool';
import { selectedConnection } from './connectionUi';
import { withConstrainedPartners } from './constraintUi';
import {
  instLocked,
  objUnpickable,
  state,
} from './core';
import { numberedInstanceNames } from './partsTab';
import {
  type HandleWork,
  startAxisWork3d,
  startHandleWork,
  type WorkRule,
} from './handleWork';
import { selectedFixtureNow } from './roomFixtures';
import { snapCandidates } from './snapSet';
import { snapPoints3d } from './tools3d';
import { viewer3d } from './view3d';
import {
  $,
  beginSelectionMove,
  heightSpanOf,
  hostRoomOf,
  instanceProbeCorners,
  selectedFeatureMoves,
  selectedMovables,
  type SelectionMove,
} from '../main';

/** What a gizmo's arrow moves: the selected whole objects (`ids`, keyed by them
 *  in `id`), in a move session begun at the press (moveSession.ts: what their
 *  rooms carry, the layers held, where each part stood, the copy Ctrl leaves,
 *  the undo step — the work's own); the axis; what it snaps to (`catches`,
 *  gathered at the press), the coordinate that lands on the round step (`ref`)
 *  and what the last step caught. */
export interface MoveSel {
  id: string;
  ids: string[];
  session: SelectionMove;
  axis: GizmoAxis;
  catches: GizmoCatch[];
  ref: number;
  caught: GizmoCatch | null;
  /** The rotate ring's work: the figure is an angle (degrees) about the hub. */
  turn?: boolean;
}

/** The arrow (or the ring) under the pointer while nothing is worked (painted lit). */
export const gizmoUi: { hover: GizmoAxis | 'turn' | null } = { hover: null };

/** The selection the gizmo moves in the 3D view, or null where it shows none:
 *  Select on; whole objects selected — not a room's feature, a bench support or a
 *  fixture, nor a lone connection (its ends stay glued); none hidden, locked or
 *  frozen; none in its vertex edit. `rise`: one of them has a height to move (a
 *  part or a room, an extruded shape, a drawn wall), so Z is offered. */
export function gizmoSelection(): { key: string; ids: string[]; rise: boolean } | null {
  if (!viewer3d || state.viewTab !== '3d' || state.tool !== 'select') return null;
  if (state.polyRoomVertexEditId || state.editVerticesId) return null;
  if (selectedConnection() || selectedSupportNow() || selectedFixtureNow() || selectedFeatureMoves()) return null;
  const ids = selectedMovables().map((m) => m.id);
  if (!ids.length || ids.some((id) => objUnpickable(id))) return null;
  for (const id of selectedOf(state, 'instance')) {
    const inst = state.drawing.instances.find((i) => i.id === id);
    if (inst && instLocked(inst)) return null;
  }
  const rise = selectedOf(state, 'instance').length + selectedOf(state, 'polygon').length + selectedOf(state, 'wall').length > 0;
  return { key: [...ids].sort().join(','), ids, rise };
}

/** Where the gizmo stands: the middle of the selection's boxes (drawing
 *  coordinates); null when nothing selected shows in the view. */
export function gizmoOrigin(): Pt3 | null {
  const b = viewer3d?.selectionBounds();
  return b ? { x: (b.min.x + b.max.x) / 2, y: (b.min.y + b.max.y) / 2, z: (b.min.z + b.max.z) / 2 } : null;
}

/** The gizmo's arrows as drawn now (css px over the 3D canvas): the ONE list the
 *  overlay paints and a press hits. Z only when something selected can rise. */
export function gizmoArrows(): GizmoArrow[] {
  const v = viewer3d, sel = gizmoSelection(), o = sel ? gizmoOrigin() : null;
  if (!v) return [];
  if (!sel) {
    const cg = cageGizmo();
    return cg ? gizmoLayout(cg.origin, (p) => v.project(p), Object.keys(cg.dirs) as GizmoAxis[], cg.dirs) : [];
  }
  if (!o) return [];
  return gizmoLayout(o, (p) => v.project(p), sel.rise ? ['x', 'y', 'z'] : ['x', 'y']);
}

/** The gizmo on the selected sculpted bench's picked cage point (handles.ts
 *  `cagePointGizmo`: its arrows along the bench, out from its faces and up),
 *  where no whole object carries one: Select on, the bench not locked. */
function cageGizmo(): ReturnType<typeof cagePointGizmo> {
  if (!viewer3d || state.viewTab !== '3d' || state.tool !== 'select') return null;
  const fx = selectedFixtureNow();
  return fx?.sculpted && !instLocked(fx.inst) ? cagePointGizmo(fx.inst, fx.id) : null;
}

/** The axis of a cage point's arrow being worked (its handle's key ends in it). */
function cageArrowAxis(w: HandleWork | null): GizmoAxis | null {
  const a = w?.hd.kind === 'cage' && w.hd.height?.along ? w.hd.key.slice(-1) : '';
  return a === 'x' || a === 'y' || a === 'z' ? a : null;
}

/** The rotate ring as drawn now (css px over the 3D canvas), where the gizmo
 *  shows: a level circle round its hub. */
export function gizmoRing(): Vec2[] {
  const v = viewer3d, o = gizmoSelection() ? gizmoOrigin() : null;
  return v && o ? ringLayout(o, (p) => v.project(p)) : [];
}

/** Whether the rotate ring is under a client point. */
export function ringAtClient(clientX: number, clientY: number): boolean {
  const r = $('view3d-canvas').getBoundingClientRect();
  return onRing(gizmoRing(), { x: clientX - r.left, y: clientY - r.top });
}

/** The arrow under a client point. */
export function gizmoArrowAtClient(clientX: number, clientY: number): GizmoArrow | null {
  const r = $('view3d-canvas').getBoundingClientRect();
  const list = gizmoArrows();
  const i = gizmoArrowAt(list, { x: clientX - r.left, y: clientY - r.top });
  return i >= 0 ? list[i] : null;
}

/** What a move along `axis` snaps to, as a drag on the plan does — gathered
 *  once at the press, the moving objects left out:
 *  - X / Y: the selection's points onto other objects' vertices in line with
 *    them (`snapCandidates`, within `perpTol` mm across the axis; each marked at
 *    its own object's height nearest the selection's base), and a part's
 *    outline flush against its room's walls, partitions and benches;
 *  - Z: its base or top onto the floor, the heights of the rooms its parts stand
 *    in (bench seats, pane, door and opening tops, the eave, the underside) and
 *    the other parts' bottoms and tops there. */
function gizmoCatches(axis: GizmoAxis, moving: ReadonlySet<string>, span: [number, number] | null, origin: Pt3, perpTol: number): GizmoCatch[] {
  const parts = selectedOf(state, 'instance').map((id) => state.drawing.instances.find((i) => i.id === id)).filter((i): i is NonNullable<typeof i> => !!i && !i.autoBenches);
  const rooms = new Map(parts.map((i) => hostRoomOf(i)).filter((r): r is NonNullable<typeof r> => !!r && !moving.has(r.id)).map((r) => [r.id, r]));
  const base = span?.[0] ?? 0;
  if (axis === 'z') {
    if (!span) return [];
    const heights: NamedHeight[] = [{ z: 0, name: tr('Floor') }];
    for (const r of rooms.values()) for (const h of hostHeights(r, origin)) heights.push({ z: h.z, name: h.name ? tr(h.name) : undefined });
    const names = numberedInstanceNames();
    for (const i of state.drawing.instances) {
      if (i.autoBenches || moving.has(i.id) || !rooms.has(hostRoomOf(i)?.id ?? '')) continue;
      const sp = heightSpanOf(i.id), name = names.get(i.id) ?? '';
      if (!sp) continue;
      heights.push({ z: sp[0], name: tr('Bottom of {name}').replace('{name}', name) }, { z: sp[1], name: tr('Top of {name}').replace('{name}', name) });
    }
    return heightCatches(span, heights, origin);
  }
  const dir = { x: GIZMO_AXES[axis].dir.x, y: GIZMO_AXES[axis].dir.y };
  const verts = selectedMovables().flatMap((m) => m.verts());
  const targets = snapPoints3d(snapCandidates(withConstrainedPartners(new Set(moving)))).map((c) => c.p);
  const out = vertexCatches(verts, targets, dir, perpTol, base, tr('Endpoint'));
  for (const i of parts) {
    const r = hostRoomOf(i);
    if (r && !moving.has(r.id)) out.push(...faceCatches(instanceProbeCorners(i), roomSnapFaces(r, (p) => instanceToWorld(r, p)), dir, base, tr('Flush')));
  }
  return out;
}

/** The step a move along a gizmo's arrow takes for the pointer's `raw` delta:
 *  free with Alt (whole mm); else what it catches within 12 px, else the round
 *  step of the zoom for the selection's corner (or base) — remembered as caught. */
export function snapGizmoStep(sel: MoveSel, raw: number, pxPerMm: number, alt: boolean): number {
  if (sel.turn) { sel.caught = null; return snapTurn(raw, alt); } // an angle: in steps of 15°, Alt to the degree
  const s = snapGizmoDelta(raw, sel.catches, 12 / pxPerMm, gridStepMm(pxPerMm), sel.ref, alt);
  sel.caught = s.caught;
  return s.d;
}

/** The handle a press on `axis`'s arrow works, standing at `origin`: a figure
 *  sliding along the axis from the hub (`HeightAxis.along`), 0 at the press —
 *  each write puts the selection, and what its rooms carry, that far along it
 *  (its move session). `perpPxPerMm`: screen px per mm across the axis on the
 *  plan, for the reach of its vertex snap. Null when the gizmo shows nothing. */
export function gizmoHandle(axis: GizmoAxis, origin: Pt3, perpPxPerMm: number, copy = false): { hd: Handle<MoveSel>; sel: MoveSel } | null {
  const s = gizmoSelection();
  if (!s) return null;
  const session = beginSelectionMove({ step: 'open', copy });
  const d = GIZMO_AXES[axis].dir;
  const hd: Handle<MoveSel> = {
    key: `move:${axis}`, kind: 'move', z: origin.z, points: [{ x: origin.x, y: origin.y }],
    height: {
      label: GIZMO_AXES[axis].label, value: 0, snaps: [], along: d,
      set: (_sel, v) => { session.moveTo({ x: d.x * v, y: d.y * v, z: d.z * v }); return null; },
    },
  };
  const moving = session.moving;
  const spans = s.ids.map((id) => heightSpanOf(id)).filter((x): x is [number, number] => !!x);
  const span: [number, number] | null = spans.length ? [Math.min(...spans.map((x) => x[0])), Math.max(...spans.map((x) => x[1]))] : null;
  const verts = selectedMovables().flatMap((m) => m.verts());
  const ref = axis === 'z' ? span?.[0] ?? 0 : verts.length ? Math.min(...verts.map((p) => (axis === 'x' ? p.x : p.y))) : 0;
  const catches = gizmoCatches(axis, moving, span, origin, 12 / Math.max(1e-3, perpPxPerMm));
  return { hd, sel: { id: s.key, ids: s.ids, session, axis, catches, ref, caught: null } };
}

/** The handle a press on the rotate ring works, the hub at `origin`: a figure in
 *  degrees, 0 at the press — each write turns the selection, and what its rooms
 *  carry, that far about the hub (its move session). Null where the gizmo shows
 *  nothing. */
export function gizmoTurnHandle(origin: Pt3, copy = false): { hd: Handle<MoveSel>; sel: MoveSel } | null {
  const s = gizmoSelection();
  if (!s) return null;
  const session = beginSelectionMove({ step: 'open', copy });
  const hub = { x: origin.x, y: origin.y };
  const hd: Handle<MoveSel> = {
    key: 'move:turn', kind: 'move', z: origin.z, points: [hub],
    height: {
      label: 'Rotate', value: 0, snaps: [],
      set: (_sel, v) => { session.turnTo(v, hub); return null; },
    },
  };
  return { hd, sel: { id: s.key, ids: s.ids, session, axis: 'z', catches: [], ref: 0, caught: null, turn: true } };
}

/** Where a gizmo's hub stood at the press, on the canvas (css px). */
function gizmoHubAt(at: Vec2, z: number): Vec2 | null {
  return viewer3d?.project({ x: at.x, y: at.y, z }) ?? null;
}

// ---- Its work: what the gizmo supplies to a handle's (handleWork.ts) ----

/** What the gizmo's work is beyond its handle (handleWork.ts `WorkRule`): an
 *  arrow's figure slides along it (the handle's own `along` reading) and snaps as
 *  a drag on the plan does (`snapGizmoStep`); the ring's is the angle about the
 *  hub (`turnAt3d`), in 15° steps, typed in degrees. Each reads its delta (`Copy
 *  ·` while Ctrl copies), rebuilds what its session moves, holds while the gizmo
 *  keys the same selection, and ends with its move session, which holds its step. */
function gizmoRule(sel: MoveSel): WorkRule {
  return {
    target: () => (gizmoSelection()?.key === sel.id ? { kind: 'move', obj: sel } : null),
    ...(sel.turn ? { figure3d: turnAt3d, typed: typedTurn } : {}),
    snap: (w, raw) => ({ v: snapGizmoStep(sel, raw, w.pxPerMm, w.alt), caught: null }),
    ids: () => [...sel.session.moving],
    hint: (w) => gizmoPrompt(sel, w.armed),
    readout: (w) => [gizmoChip(sel, w.value ?? 0)],
    endStep: (how) => sel.session.end(how),
    gizmo: sel,
  };
}

/** The ring's angle under the pointer, from where it was pressed (degrees). */
function turnAt3d(w: HandleWork): number | null {
  if (!viewer3d || !w.planRef) return null;
  const r = viewer3d.rayAt(w.pointer.x, w.pointer.y), p = rayToLevel(r.o, r.d, w.hd.z);
  return p ? turnBetween(w.at, w.planRef, p) : null;
}

/** An angle typed for the ring, in degrees. */
function typedTurn(text: string): { v: number } | { refusal: string } {
  const deg = Number(text.replace(',', '.').replace('°', ''));
  return Number.isFinite(deg) ? { v: deg } : { refusal: tr('Type the angle in degrees.') };
}

/** The gizmo's chip: the delta (or the angle) it reads, what it caught. */
function gizmoChip(sel: MoveSel, v: number): string {
  const c = sel.caught;
  const line = sel.turn ? `${tr('Rotate')} ${turnReadout(v)}` : gizmoReadout(sel.axis, v, drawingDimStyle(state.drawing), c && Math.abs(c.d - v) < 0.5 ? c.name : undefined);
  return sel.session.copying ? `${tr('Copy')} · ${line}` : line;
}

/** The bar's prompt while the gizmo is worked. */
function gizmoPrompt(sel: MoveSel, armed: boolean): string {
  if (sel.turn) {
    return armed
      ? tr('Rotate about the vertical — move to set the angle, click to drop it, or type it in degrees and press Enter (Esc cancels)')
      : tr('Rotate about the vertical — drag to set the angle (15° steps, Alt for any), or type it in degrees and press Enter (Esc cancels)');
  }
  const text = sel.session.copying
    ? (armed
      ? tr('Copy along {axis} — move to set the distance, click to drop it, or type it and press Enter (Esc cancels)')
      : tr('Copy along {axis} — drag to set the distance, or type it and press Enter (Esc cancels)'))
    : (armed
      ? tr('Move along {axis} — move to set the distance, click to drop it, or type it and press Enter (Esc cancels)')
      : tr('Move along {axis} — drag to set the distance, or type it and press Enter (Esc cancels)'));
  return text.replace('{axis}', GIZMO_AXES[sel.axis].name);
}

/** A press on one of the arrows: its work begins — a figure along the arrow's
 *  axis, through the hub (`rayToAxis`), 0 at the press. */
export function startArrowWork3d(g: GizmoArrow, e: PointerEvent): void {
  if (!gizmoSelection()) {
    // A cage point's arrow: its handle's work, as the point's own handle is worked.
    const cg = cageGizmo();
    if (cg && cg.dirs[g.axis]) startAxisWork3d(cg.handle(g.axis), cg.origin, g, e);
    return;
  }
  const v = viewer3d, o = gizmoOrigin();
  if (!v || !o) return;
  const d = GIZMO_AXES[g.axis].dir;
  const tip = v.project({ x: o.x + d.x * 100, y: o.y + d.y * 100, z: o.z + d.z * 100 });
  // Across the axis on the plan (Y for X, X for Y): the reach of its vertex snap.
  const across = g.axis === 'z' ? null : v.project({ x: o.x + d.y * 100, y: o.y + d.x * 100, z: o.z });
  const made = gizmoHandle(g.axis, o, across ? Math.hypot(across.x - g.from.x, across.y - g.from.y) / 100 : 1, e.ctrlKey || e.metaKey);
  if (!made) return;
  const ray = v.rayAt(e.clientX, e.clientY);
  startHandleWork({
    space: '3d', ref: { kind: 'move', id: made.sel.id }, hd: made.hd, rule: gizmoRule(made.sel), pick: 0, at: { x: o.x, y: o.y },
    press: { x: e.clientX, y: e.clientY }, alt: e.altKey, planOk: false,
    up: tip ? { x: tip.x - g.from.x, y: tip.y - g.from.y } : { x: 0, y: 0 },
    pxPerMm: Math.max(1e-3, tip ? Math.hypot(tip.x - g.from.x, tip.y - g.from.y) / 100 : 0), planPxPerMm: 1,
    zRef: rayToAxis(ray.o, ray.d, o, d), planRef: null, viewSnaps: [],
  }, made.sel.session.start);
  gizmoUi.hover = null;
  v.setHover(null);
  $('view3d-canvas').style.cursor = 'move';
}

/** A press on the rotate ring: its work begins — an angle about the hub, read
 *  where the pointer's ray meets the hub's level, 0 at the press. None while the
 *  view looks along that level (no angle can be read). */
export function startRingWork3d(e: PointerEvent): void {
  const v = viewer3d, o = gizmoOrigin();
  if (!v || !o) return;
  const ray = v.rayAt(e.clientX, e.clientY), at = rayToLevel(ray.o, ray.d, o.z);
  const made = at ? gizmoTurnHandle(o, e.ctrlKey || e.metaKey) : null;
  if (!at || !made) return;
  startHandleWork({
    space: '3d', ref: { kind: 'move', id: made.sel.id }, hd: made.hd, rule: gizmoRule(made.sel), pick: 0, at: { x: o.x, y: o.y },
    press: { x: e.clientX, y: e.clientY }, alt: e.altKey, planOk: false, up: { x: 0, y: 0 },
    pxPerMm: 1, planPxPerMm: 1, zRef: null, planRef: at, viewSnaps: [],
  }, made.sel.session.start);
  gizmoUi.hover = null;
  v.setHover(null);
  $('view3d-canvas').style.cursor = 'move';
}

/** Paint the gizmo on the 3D overlay (`c`, css px), under the handles: its
 *  arrows and ring, the one hovered or worked lit — and while its work has moved,
 *  a dashed guide from where the hub stood and a diamond on what it caught —,
 *  listed for the web cases (`data-gizmo`, `data-ring`). None while another
 *  handle is worked (`w`). Returns where its own work's chip stands (css px). */
export function paintGizmo3d(c: CanvasRenderingContext2D, cv: HTMLElement, w: HandleWork | null): Vec2 | null {
  const sel = w?.rule.gizmo, cageAxis = cageArrowAxis(w);
  const arrows = w && !sel && !cageAxis ? [] : gizmoArrows();
  const ring = w && !sel ? [] : gizmoRing();
  const turning = !!sel?.turn;
  const hub = sel && w?.moved && !turning ? gizmoHubAt(w.at, w.hd.z) : null;
  const caught = sel?.caught && w?.moved && Math.abs(sel.caught.d - (w.value ?? 0)) < 0.5 ? viewer3d?.project(sel.caught.at) ?? null : null;
  const lit = sel ? (turning ? null : sel.axis) : cageAxis ?? (gizmoUi.hover === 'turn' ? null : gizmoUi.hover);
  drawGizmo(c, arrows, lit, hub, caught, { pts: ring, lit: turning || (!w && gizmoUi.hover === 'turn') });
  cv.dataset.gizmo = arrows.map((g) => `${g.axis}@${Math.round(g.from.x)},${Math.round(g.from.y)}>${Math.round(g.to.x)},${Math.round(g.to.y)}`).join(' ');
  cv.dataset.ring = ring.filter((_, i) => i % 6 === 0).map((q) => `${Math.round(q.x)},${Math.round(q.y)}`).join(' ');
  if (cageAxis) return arrows.find((g) => g.axis === cageAxis)?.to ?? null;
  if (!sel) return null;
  return turning ? ring[0] ?? null : arrows.find((g) => g.axis === sel.axis)?.to ?? hub;
}
